import { useState } from 'react';
import type { Goal, GoalKind } from '../lib/ocean';

// ─────────────────────────────────────────────────────────────────────────────
// This week's class goals (ocean_goals): things the whole class does together in
// the ocean, paid in sand dollars to everyone once reached. Students see them;
// the teacher also adds and removes them.
// ─────────────────────────────────────────────────────────────────────────────

export const GOAL_KINDS: Record<GoalKind, { icon: string; label: (n: number) => string }> = {
  fish: { icon: '🎣', label: n => `Catch ${n} fish` },
  chests: { icon: '🧰', label: n => `Open ${n} treasure chests` },
  beasts: { icon: '⚔️', label: n => `Beat ${n} sea beasts` },
  kraken: { icon: '🐙', label: n => (n === 1 ? 'Defeat the Kraken' : `Defeat the Kraken ${n} times`) },
  habitat: { icon: '🏡', label: n => `${n} fish move into habitats` },
  quests: { icon: '📜', label: n => `Finish ${n} quests` },
};

type Props = {
  goals: Goal[];
  /** The teacher's version: add and remove. */
  onAdd?: (kind: GoalKind, target: number, reward: number) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
};

export default function ClassGoals({ goals, onAdd, onDelete }: Props) {
  const [kind, setKind] = useState<GoalKind>('fish');
  const [target, setTarget] = useState(30);
  const [reward, setReward] = useState(5);
  const [busy, setBusy] = useState(false);
  return (
    <div className="class-goals">
      {goals.length === 0 && <p className="small muted" style={{ margin: 0 }}>{onAdd ? 'No goals this week yet. Add one below.' : 'No class goals this week.'}</p>}
      {goals.map(g => {
        const k = GOAL_KINDS[g.kind], done = g.progress >= g.target;
        return (
          <div key={g.id} className={`goal-row${done ? ' done' : ''}`}>
            <div className="row">
              <strong>{k.icon} {k.label(g.target)}</strong>
              <span className="spacer" />
              <span className="small">{Math.min(g.progress, g.target)}/{g.target}{g.reward ? ` · 🪙 ${g.reward} each` : ''}{done ? ' · ✅ Reached!' : ''}</span>
              {onDelete && <button type="button" className="icon small" aria-label="Remove goal" onClick={() => void onDelete(g.id)}>✕</button>}
            </div>
            <div className="habitat-bar"><span style={{ width: `${Math.min(100, (g.progress / g.target) * 100)}%` }} /></div>
          </div>
        );
      })}
      {onAdd && (
        <div className="row goal-add">
          <select value={kind} onChange={e => setKind(e.target.value as GoalKind)} aria-label="Goal">
            {(Object.keys(GOAL_KINDS) as GoalKind[]).map(k => <option key={k} value={k}>{GOAL_KINDS[k].icon} {GOAL_KINDS[k].label(0).replace(' 0', ' N').replace('Defeat the Kraken', 'Defeat the Kraken N×')}</option>)}
          </select>
          <label className="small">Target <input type="number" min={1} max={10000} value={target} onChange={e => setTarget(Math.max(1, Math.round(Number(e.target.value) || 1)))} style={{ width: 80 }} /></label>
          <label className="small">🪙 each <input type="number" min={0} max={1000} value={reward} onChange={e => setReward(Math.max(0, Math.round(Number(e.target.value) || 0)))} style={{ width: 70 }} /></label>
          <button type="button" className="primary small" disabled={busy}
            onClick={async () => { setBusy(true); try { await onAdd(kind, target, reward); } finally { setBusy(false); } }}>Add goal</button>
        </div>
      )}
    </div>
  );
}
